import Cards from "./Cards";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import UPI from "./UPI";
import DataContext from "../contexts/DataProvider";
import FormDataContext from "../contexts/FormData";
import { FaCreditCard, FaUniversity, FaWallet, FaSearch, FaCheck, FaShieldAlt } from "react-icons/fa";
import analytics from "../helper/analytics";
import Offers from "./Offers";

// Maps the internal tab key to a stable analytics section/method name.
const METHOD_KEY = {
  "UPI": "upi",
  "Add Credit/Debit": "card",
  "Net Banking": "netbanking",
  "Wallets": "wallet",
};

const UpiIcon = () => (
  <svg width="16" height="18" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 2 }}>
    {/* Left arrow outline */}
    <path d="M39 6 L72 43 L38 70 L19 97 Z"
      fill="none" stroke="currentColor" strokeWidth="7"
      strokeLinejoin="round" strokeLinecap="round" />
    {/* Right arrow outline — offset right+down */}
    <path d="M58 4 L86 47 L56 72 L35 99 Z"
      fill="none" stroke="currentColor" strokeWidth="7"
      strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);


const PaymentOptions = ({ setIsNavigating }) => {
  const [selectedPayType, setSelectedPayType] = useState("");
  const [bankValue, setBankValue] = useState(null);
  const [walletValue, setWalletValue] = useState(null);
  const [bankSearch, setBankSearch] = useState("");
  const [walletSearch, setWalletSearch] = useState("");
  const [intialPType, setIntialPType] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const bottomRef = useRef(null);
  const scrollPanelRef = useRef(null);
  // Fraud signal: distinct banks / wallets tried this session.
  const seenBanksRef = useRef(new Set());
  const seenWalletsRef = useRef(new Set());

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const { formData, setFormData } = useContext(FormDataContext);
  const { data, setData } = useContext(DataContext);

  const bankList = data.bankList || [];
  const walletList = data.walletList || [];

  useEffect(() => {
    if (data?.bins !== undefined) {
      if (data.upiDQR || data.upiCollect || data.upiDeepLink) {
        setIntialPType("UPI");
      } else if (data.bins?.length > 0 || data.card) {
        setIntialPType("Add Credit/Debit");
      } else if (data.netbanking) {
        setIntialPType("Net Banking");
      } else if (data.wallet) {
        setIntialPType("Wallets");
      } else {
        setIntialPType("NONE");
      }
    }
  }, [data?.bins, data?.upiDQR, data?.upiCollect, data?.upiDeepLink, data?.card, data?.netbanking, data?.wallet]);
  useEffect(() => {
    if (intialPType && !selectedPayType) {
      setSelectedPayType(intialPType);
      const mopMap = {
        "UPI": "UPI",
        "Add Credit/Debit": "CARD",
        "Net Banking": "NETBANKING",
        "Wallets": "WALLET"
      };
      setFormData(prev => ({ ...prev, modeOfPayment: mopMap[intialPType] }));
    }
  }, [intialPType]);

  useEffect(() => {
    setBankValue(null);
    setWalletValue(null);
    setBankSearch("");
    setWalletSearch("");
  }, [selectedPayType]);

  // Enter the section for the selected non-UPI method (UPI owns its own sub-sections
  // — collect / qr / deeplink — inside UPI.js). useLayoutEffect so the section updates
  // before mount-time child events get tagged. method_selected is logged only on a
  // deliberate user switch (handleTabClick), NOT on the automatic default selection.
  useLayoutEffect(() => {
    const method = METHOD_KEY[selectedPayType];
    if (method && method !== "upi") analytics.enterSection(method);
  }, [selectedPayType]);

  const tileColors = ["#1E56C8","#6366f1","#f59e0b","#ef4444","#3b82f6","#8b5cf6","#ec4899","#14b8a6","#f97316","#0ea5e9"];
  const getTileColor = (name) => tileColors[name.charCodeAt(0) % tileColors.length];
  const getInitials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const filteredBanks = bankList.filter((b) =>
    b.label.toLowerCase().includes(bankSearch.toLowerCase())
  );
  const filteredWallets = walletList.filter((w) =>
    w.label.toLowerCase().includes(walletSearch.toLowerCase())
  );


  const nextStep = formData?.nextStep === "CHECKSTATUS" || data.pg === 12;

  const tabs = [
    { key: "UPI",              label: "UPI",         Icon: FaCreditCard, show: data.upiDQR || data.upiCollect || data.upiDeepLink },
    { key: "Add Credit/Debit", label: "Card",         Icon: FaCreditCard, show: data.card },
    { key: "Net Banking",      label: "Net Banking",  Icon: FaUniversity, show: data.netbanking },
    { key: "Wallets",          label: "Wallet",       Icon: FaWallet,     show: data.wallet },
  ].filter((t) => t.show);

  const handleTabClick = (tab) => {
    const mopMap = {
      "UPI": "UPI",
      "Add Credit/Debit": "CARD",
      "Net Banking": "NETBANKING",
      "Wallets": "WALLET"
    };
    if (tab.key !== selectedPayType) {
      setFormData(prev => ({ 
        ...prev, 
        type: null, 
        modeOfPayment: mopMap[tab.key],
        cardNumber: "" 
      }));
    }
    setSelectedPayType(tab.key);
    setData({ ...data, errorMessage: "" });
    // Log the deliberate method switch (not the auto-default). Enter the destination
    // section first (non-UPI) so the event carries the right section, not the previous one.
    const switched = METHOD_KEY[tab.key];
    if (switched && tab.key !== selectedPayType) {
      if (switched !== "upi") analytics.enterSection(switched);
      analytics.track("method_selected", { method: switched });
      analytics.flush("checkpoint_method");
    }
    if (scrollPanelRef.current) scrollPanelRef.current.scrollTop = 0;
  };

  return (
    <div className="flex flex-col h-full min-h-0">

      {/* ── Available Offers strip + "Save more" sheet (Razorpay-style) ── */}
      <Offers offers={data.offers} />

      <div className="flex flex-col flex-1 min-h-0 rounded-2xl shadow-sm bg-white mx-2 overflow-hidden">
      <div className={`${isMobile ? "flex flex-col" : "flex flex-row"} flex-1 min-h-0 overflow-hidden`}>

      {/* ── Mobile: horizontal top tab bar ── */}
      {isMobile && (
        <div className="flex flex-row flex-shrink-0 border-b border-gray-100 bg-white">
          {tabs.map((tab) => {
            const isDisabled = nextStep && tab.key !== "UPI";
            const isActive = selectedPayType === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                disabled={isDisabled}
                onClick={() => { if (!isDisabled) handleTabClick(tab); }}
                className={`relative flex-1 flex flex-col items-center pt-2.5 pb-2 gap-1 text-[10px] font-semibold transition-all duration-200 ${
                  isDisabled ? "text-gray-300 opacity-40 cursor-not-allowed"
                  : isActive  ? "text-[#1a6cb8]"
                  : "text-gray-400 active:bg-gray-50"
                }`}
              >
                {isActive && <div className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-t-full bg-[#1a6cb8]" />}
                <div className={`flex items-center justify-center text-[18px] ${isActive ? "text-[#1a6cb8]" : "text-gray-400"}`}>
                  {tab.key === "UPI" ? <UpiIcon /> : <tab.Icon />}
                </div>
                <span className="leading-tight">{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Desktop: left sidebar ── */}
      {!isMobile && (
        <div className="flex flex-col border-r border-gray-100 bg-gray-50/60 flex-shrink-0 py-3 w-[80px] sm:w-[100px]">
          {tabs.map((tab) => {
            const isDisabled = nextStep && tab.key !== "UPI";
            const isActive = selectedPayType === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                disabled={isDisabled}
                onClick={() => { if (!isDisabled) handleTabClick(tab); }}
                className={`relative flex flex-col items-center py-4 px-2 text-[10px] sm:text-[11px] font-semibold transition-all duration-200 w-full ${
                  isDisabled ? "text-gray-200 cursor-not-allowed opacity-40"
                  : isActive  ? "text-[#DE554D] bg-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-white/60"
                }`}
              >
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r bg-[#DE554D]" />}
                <div className={`mb-1.5 flex items-center justify-center ${isActive ? "text-[#DE554D]" : "text-gray-400"} text-[20px] sm:text-[22px]`}>
                  {tab.key === "UPI" ? <UpiIcon /> : <tab.Icon />}
                </div>
                <span className="leading-tight text-center">{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Content panel */}
      <div
        ref={scrollPanelRef}
        className="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-3 tab-panel-enter hide-scrollbar"
        key={selectedPayType}
      >
        {selectedPayType === "NONE" && (
          <div className="flex flex-col flex-1 items-center justify-center gap-3 py-10 text-gray-400">
            <svg viewBox="0 0 24 24" className="w-10 h-10 opacity-30" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p className="text-sm font-medium text-gray-400">No payment methods available</p>
          </div>
        )}

        {selectedPayType === "UPI" && (
          <div className="flex flex-col flex-1 min-h-0 w-full">
            <UPI selectedPayType={selectedPayType} bottomRef={bottomRef} setIsNavigating={setIsNavigating} />
          </div>
        )}

        {selectedPayType === "Add Credit/Debit" && data.card && !nextStep && (
          <Cards selectedPayType={selectedPayType} />
        )}

        {selectedPayType === "Net Banking" && data.netbanking && !nextStep && (
          <div className="flex flex-col gap-2 w-full">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                type="text"
                className="input-modern w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 bg-gray-50"
                placeholder="Search or select your bank"
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                style={{ fontSize: 16 }}
              />
            </div>
            {/* Selected bank chip */}
            {bankValue && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                   style={{ background: "rgba(30,86,200,0.12)", border: "1px solid rgba(30,86,200,0.35)" }}>
                <FaCheck className="text-[#1E56C8] flex-shrink-0" style={{ fontSize: 11 }} />
                <span className="text-xs font-semibold text-[#1340A0]">Selected:</span>
                <span className="text-xs font-medium text-gray-700 truncate">{bankValue.label}</span>
              </div>
            )}
            {/* Bank list */}
            {filteredBanks.length > 0 ? (
              <div className="flex flex-col divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                {filteredBanks.map((bank) => {
                  const isSelected = bankValue != null && bank.value?.bankId != null && bankValue?.value?.bankId === bank.value?.bankId;
                  return (
                    <button
                      key={bank.value?.bankId}
                      type="button"
                      onClick={() => {
                        setData({ ...data, errorMessage: "" });
                        seenBanksRef.current.add(bank.value?.bankId);
                        analytics.track("bank_selected", { attemptNo: seenBanksRef.current.size, bankId: bank.value?.bankId, search: bankSearch.length > 0 });
                        setFormData({ type: "netBanking", bank: bank.value, modeOfPayment: "NETBANKING" });
                        setBankValue(bank);
                        setWalletValue(null);
                        if (scrollPanelRef.current) scrollPanelRef.current.scrollTop = 0;
                      }}
                      className={`flex items-center gap-3 px-3 py-2.5 text-left transition-all active:scale-[0.99] ${
                        isSelected
                          ? "bg-[#eff6ff] border-l-2 border-l-[#1E56C8]"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <span className={`text-sm font-medium flex-1 ${isSelected ? "text-[#1340A0]" : "text-gray-700"}`}>
                        {bank.label}
                      </span>
                      {isSelected && <span className="text-[#1E56C8] text-xs">✓</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                <FaUniversity style={{ fontSize: 28, opacity: 0.3 }} />
                <p className="text-sm">No banks found</p>
              </div>
            )}
            {data.errorMessage && (
              <p className="text-xs px-1 text-[#E80F21]">{data.errorMessage}</p>
            )}
          </div>
        )}

        {selectedPayType === "Wallets" && data.wallet && !nextStep && (
          <div className="flex flex-col gap-2 w-full">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                type="text"
                className="input-modern w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 bg-gray-50"
                placeholder="Search wallet..."
                value={walletSearch}
                onChange={(e) => setWalletSearch(e.target.value)}
                style={{ fontSize: 16 }}
              />
            </div>
            {/* Selected wallet chip */}
            {walletValue && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                   style={{ background: "rgba(30,86,200,0.12)", border: "1px solid rgba(30,86,200,0.35)" }}>
                <FaCheck className="text-[#1E56C8] flex-shrink-0" style={{ fontSize: 11 }} />
                <span className="text-xs font-semibold text-[#1340A0]">Selected:</span>
                <span className="text-xs font-medium text-gray-700 truncate">{walletValue.label}</span>
              </div>
            )}
            {/* Wallet list */}
            {filteredWallets.length > 0 ? (
              <div className="flex flex-col divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                {filteredWallets.map((wallet) => {
                  const isSelected = walletValue != null && wallet.value?.bankId != null && walletValue?.value?.bankId === wallet.value?.bankId;
                  return (
                    <button
                      key={wallet.value?.bankId}
                      type="button"
                      onClick={() => {
                        setData({ ...data, errorMessage: "" });
                        seenWalletsRef.current.add(wallet.value?.bankId);
                        analytics.track("wallet_selected", { attemptNo: seenWalletsRef.current.size, walletId: wallet.value?.bankId, search: walletSearch.length > 0 });
                        setFormData({ type: "wallet", bank: wallet.value, modeOfPayment: "WALLET" });
                        setWalletValue(wallet);
                        setBankValue(null);
                        if (scrollPanelRef.current) scrollPanelRef.current.scrollTop = 0;
                      }}
                      className={`flex items-center gap-3 px-3 py-2.5 text-left transition-all active:scale-[0.99] ${
                        isSelected
                          ? "bg-[#eff6ff] border-l-2 border-l-[#1E56C8]"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <span className={`text-sm font-medium flex-1 ${isSelected ? "text-[#1340A0]" : "text-gray-700"}`}>
                        {wallet.label}
                      </span>
                      {isSelected && <span className="text-[#1E56C8] text-xs">✓</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                <FaWallet style={{ fontSize: 28, opacity: 0.3 }} />
                <p className="text-sm">No wallets found</p>
              </div>
            )}
            {data.errorMessage && (
              <p className="text-xs px-1 text-[#E80F21]">{data.errorMessage}</p>
            )}
          </div>
        )}
      </div>

      <div ref={bottomRef} />
      </div>

      {/* ── Compliance disclaimer (from disclaimerNote) — pinned to bottom of the card ── */}
      {data.disclaimerNote && (
        <div className="flex-shrink-0 border-t border-gray-100 px-2 py-1.5">
          <div className="flex items-start gap-2 rounded-lg px-2.5 py-1.5"
               style={{ background: "rgba(30,86,200,0.06)", border: "1px solid rgba(30,86,200,0.14)" }}>
            <FaShieldAlt style={{ color: "#1E56C8", fontSize: 11 }} className="flex-shrink-0 mt-0.5" />
            <p className="text-[10px] leading-snug text-[#475569]">{data.disclaimerNote}</p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PaymentOptions;
